import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { X } from 'lucide-react'
import { useApi } from '@/hooks/useAuth'
import type { Person } from '@/types/person'

const memorySchema = z.object({
  name: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  description: z.string().min(1, 'Description is required').max(5000, 'Description must be less than 5000 characters'),
  date: z.string().min(1, 'Date is required'),
})

type MemoryForm = z.infer<typeof memorySchema>

export default function NewMemoryPage() {
  const [error, setError] = useState('')
  const [people, setPeople] = useState<Person[]>([])
  const [selectedPeople, setSelectedPeople] = useState<string[]>([])
  const { createMemory, getPeople } = useApi()
  const navigate = useNavigate()

  useEffect(() => {
    getPeople().then(setPeople).catch(() => setPeople([]))
  }, [getPeople])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MemoryForm>({
    resolver: zodResolver(memorySchema),
  })

  const onSubmit = async (data: MemoryForm) => {
    try {
      setError('')
      await createMemory({ ...data, people: selectedPeople })
      navigate('/memories')
    } catch {
      setError('Failed to create memory')
    }
  }

  const togglePerson = (personId: string) => {
    setSelectedPeople(prev =>
      prev.includes(personId) ? prev.filter(id => id !== personId) : [...prev, personId]
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Create New Memory</CardTitle>
          <CardDescription>Capture a remarkable moment from your life</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Title</Label>
              <Input id="name" placeholder="A wonderful day with my girlfriend" {...register('name')} />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input id="date" type="date" {...register('date')} />
              {errors.date && <p className="text-sm text-destructive">{errors.date.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe this special moment..."
                className="min-h-[200px]"
                {...register('description')}
              />
              {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
            </div>
            {people.length > 0 && (
              <div className="space-y-2">
                <Label>People</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-md min-h-[60px]">
                  {selectedPeople.length === 0 && (
                    <span className="text-muted-foreground text-sm">Select people associated with this memory</span>
                  )}
                  {selectedPeople.map(id => {
                    const person = people.find(p => p.id === id)
                    if (!person) return null
                    return (
                      <Badge key={id} variant="secondary" className="pl-2 pr-1 py-1">
                        {person.name}
                        <button
                          type="button"
                          onClick={() => togglePerson(id)}
                          className="ml-1 hover:bg-destructive/20 rounded"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )
                  })}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {people
                    .filter(p => !selectedPeople.includes(p.id))
                    .map(person => (
                      <Badge
                        key={person.id}
                        variant="outline"
                        className="cursor-pointer hover:bg-secondary"
                        onClick={() => togglePerson(person.id)}
                      >
                        + {person.name}
                      </Badge>
                    ))}
                </div>
              </div>
            )}
            <div className="flex gap-4">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Memory'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/memories')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}